/** Display formatting from decimal strings (no float round-trips). */
import { D } from './decimal.js';
function group(intPart) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/** Format with thousands separators and a fixed number of decimals. */
export function fmt(x, dp, mode = 'half_up') {
    if (x === null || x === undefined || x === '')
        return '—';
    let s;
    try {
        s = D.from(x).toFixed(dp, mode);
    }
    catch {
        return '—';
    }
    const neg = s.startsWith('-');
    if (neg)
        s = s.slice(1);
    const [i, f] = s.split('.');
    return (neg ? '-' : '') + group(i ?? '0') + (f !== undefined ? '.' + f : '');
}
/** Signed format: "+1.23" / "-1.23". */
export function fmtSigned(x, dp) {
    if (x === null || x === undefined || x === '')
        return '—';
    const v = D.from(x);
    const s = fmt(v, dp);
    return v.isNeg() || s.startsWith('-') ? s : '+' + s;
}
export function fmtPct(x, dp = 2) {
    if (x === null || x === undefined || x === '')
        return '—';
    return fmtSigned(x, dp) + '%';
}
/** Compact amount without a unit: 12.41M, 3.46B, 812.30K, 0.00. Use fmtCompactUsd for money. */
export function fmtCompact(x) {
    if (x === null || x === undefined || x === '')
        return '—';
    const v = D.from(x).abs();
    const units = [
        ['T', D.from(1e12)],
        ['B', D.from(1e9)],
        ['M', D.from(1e6)],
        ['K', D.from(1e3)],
    ];
    for (const [u, n] of units) {
        if (v.gte(n))
            return fmt(v.div(n), 2) + u;
    }
    return fmt(v, 2);
}
/** Compact USD: $12.41M, $3.46B, $2.10T, $812.30K. */
export function fmtCompactUsd(x) {
    const s = fmtCompact(x);
    return s === '—' ? s : '$' + s;
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');
function toDate(x) {
    if (x === null || x === undefined || x === '')
        return null;
    const d = x instanceof Date ? x : new Date(x);
    return Number.isNaN(d.getTime()) ? null : d;
}
/** "06 Oct 2026" in the viewer's local time zone. One format app-wide: the month is a word so day and month can't be confused. */
export function fmtDate(x) {
    const d = toDate(x);
    return d ? `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '—';
}
/** "16:50:58" (24h, local time). */
export function fmtTime(x) {
    const d = toDate(x);
    return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` : '—';
}
/** "06 Oct 2026, 16:50:58", or "06 Oct 16:50:58" with `year: false` for dense tables. */
export function fmtDateTime(x, opts = {}) {
    const d = toDate(x);
    if (!d)
        return '—';
    return opts.year === false ? `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${fmtTime(d)}` : `${fmtDate(d)}, ${fmtTime(d)}`;
}
/** USD amount: "$1,234.56" / "-$1,234.56". */
export function fmtUsd(x, dp = 2) {
    const s = fmt(x, dp);
    if (s === '—')
        return s;
    return s.startsWith('-') ? '-$' + s.slice(1) : '$' + s;
}
/** Instrument price: "$1,234.50" when priced in USD, a bare number otherwise (USD/JPY "150.123"). */
export function fmtPrice(x, dp = 2, ccy = 'USD') {
    return ccy === 'USD' ? fmtUsd(x, dp) : fmt(x, dp);
}
/** Signed USD amount for PnL: "+$1.23" / "-$1.23". */
export function fmtUsdSigned(x, dp = 2) {
    const s = fmtUsd(x, dp);
    if (s === '—' || s.startsWith('-') || /^\$[0.,]+$/.test(s))
        return s;
    return '+' + s;
}
/** Readable trade number, shared by a pending order, the position it opens and its partial-close parts: "#100245" ('' when none). */
export function fmtTradeId(id) {
    return id == null ? '' : `#${id}`;
}
/** " · Order #100245" to append to a confirmation message ('' when there is no number). */
export function tradeIdSuffix(id) {
    return id == null ? '' : ` · Order ${fmtTradeId(id)}`;
}
const CURRENCIES = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'HKD', 'SGD', 'SEK', 'NOK', 'DKK', 'MXN', 'ZAR', 'TRY', 'CNH', 'PLN', 'HUF', 'CZK']);
// Asset class per symbol, filled from the instrument list (the web's market store, the API's instrument cache), so a bare
// symbol can be labelled the way its market is traded.
const assetClasses = new Map();
/** Records each instrument's asset class for the label helpers below. */
export function registerAssetClasses(list) {
    for (const i of list)
        assetClasses.set(i.symbol, i.assetClass);
}
/** Asset class of a registered symbol, undefined before the instrument list has loaded. */
export const assetClassOf = (symbol) => assetClasses.get(symbol);
/** Classes traded against USDT, the account currency; the rest are shown by their own name (GOLD, AAPL, SPX500). */
const USDT_QUOTED = new Set(['CRYPTO', 'MEME']);
/**
 * The two legs of an instrument: a currency pair such as USDJPY splits in two; crypto is quoted against USDT; gold,
 * stocks and indices have no quote leg (''). A symbol whose class isn't known yet is treated as crypto.
 */
export function pairParts(symbol, assetClass = assetClasses.get(symbol)) {
    if (/^[A-Z]{6}$/.test(symbol) && CURRENCIES.has(symbol.slice(0, 3)) && CURRENCIES.has(symbol.slice(3)))
        return [symbol.slice(0, 3), symbol.slice(3)];
    return !assetClass || USDT_QUOTED.has(assetClass) ? [symbol, 'USDT'] : [symbol, ''];
}
/** Instrument label: "BTC/USDT", "USD/JPY", "GOLD", "AAPL". */
export function pairLabel(symbol, assetClass) {
    const [base, quote] = pairParts(symbol, assetClass);
    return quote ? `${base}/${quote}` : base;
}
/** Market codes traders know commodities by, shown beside the symbol and matched by search. */
const COMMODITY_TAGS = {
    GOLD: 'XAU/USD',
    SILVER: 'XAG/USD',
    PLATINUM: 'XPT/USD',
    PALLADIUM: 'XPD/USD',
    COPPER: 'XCU/USD',
    OIL: 'WTI/USD',
    BRENT: 'XBR/USD',
    NATGAS: 'NG/USD',
};
/** Secondary market code for an instrument ("XAU/USD" for GOLD), or null when the symbol says it all. */
export const instrumentTag = (symbol) => COMMODITY_TAGS[symbol] ?? null;
//# sourceMappingURL=format.js.map